import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FaExclamationTriangle, 
  FaNetworkWired, 
  FaTrash, 
  FaServer, 
  FaShieldAlt,
  FaTimes
} from 'react-icons/fa';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";
import { useAuth } from '../../components/auth/AuthContext';

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'firewall-details-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'firewall-details-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'firewall-details-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'User profile not found. Please refresh and try again',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'INVALID_USER_PROFILE': 'Invalid user profile format. Please contact support',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again later',
  'DB_ERROR': 'Service temporarily unavailable. Please try again later',
  'API_ERROR': 'Service temporarily unavailable. Please try again later',
  'INVALID_API_RESPONSE': 'Service error. Please try again later',
  'API_CONNECTION_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'NETWORK_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'INVALID_ID_FORMAT': 'Invalid request format. Please try again',
  'INVALID_DATA': 'Invalid input data. Please check your entries and try again',
  'INVALID_REQUEST': 'Invalid request format. Please try again',
  'MISSING_FIELDS': 'Please fill in all required fields',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'INVALID_RULE_PARAMETERS': 'Invalid firewall rule parameters. Please check your input',
  'INVALID_JSON': 'Invalid request format. Please check your input',
  'UNEXPECTED_PAYLOAD': 'Unexpected request data. Please try again',
  'UNEXPECTED_BODY': 'Invalid request format. Please try again',
  'INVALID_RULE_ID_FORMAT': 'Invalid rule ID format. Please try again',
  'FIREWALL_LIST_FAILED': 'Unable to load firewalls. Please refresh the page',
  'FIREWALL_DETAILS_FAILED': 'Unable to load firewall details. Please try again',
  'FIREWALL_CREATE_FAILED': 'Unable to create firewall. Please try again',
  'FIREWALL_DELETE_FAILED': 'Unable to delete firewall. Please try again',
  'FIREWALL_NOT_FOUND': 'Firewall not found. It may have been deleted',
  'FIREWALL_LIMIT_EXCEEDED': 'You have reached the maximum limit of 50 firewalls',
  'FIREWALL_HAS_ATTACHMENTS': 'Firewall is attached to VMs. Please detach before deleting',
  'FIREWALL_IN_USE': 'Firewall is currently in use. Please detach all instances before deleting',
  'RESOURCE_ALREADY_EXISTS': 'A firewall with this name already exists',
  'FIREWALL_RULE_ADD_FAILED': 'Unable to add firewall rule. Please try again',
  'FIREWALL_RULE_DELETE_FAILED': 'Unable to remove firewall rule. Please try again',
  'FIREWALL_RULE_NOT_FOUND': 'Firewall rule not found. It may have been removed',
  'SECURITY_RULE_ADD_FAILED': 'Unable to add security rule. Please try again',
  'SECURITY_RULE_DELETE_FAILED': 'Unable to remove security rule. Please try again',
  'SECURITY_RULE_ALREADY_EXISTS': 'This security rule already exists for this instance',
  'RULE_NOT_FOUND': 'Security rule not found. It may have been removed',
  'VM_LIST_FAILED': 'Unable to load virtual machines. Please refresh the page',
  'VM_NOT_FOUND': 'Virtual machine not found. It may have been deleted',
  'VM_NOT_ACTIVE': 'Virtual machine must be active to perform this action',
  'VM_FIREWALLS_FAILED': 'Unable to load VM firewall information. Please try again',
  'VM_EVENTS_FAILED': 'Unable to load VM events. Please try again',
  'FIREWALL_ATTACH_FAILED': 'Unable to attach firewall to VMs. Please try again',
  'NO_VMS_SELECTED': 'Please select at least one virtual machine',
  'INVALID_VM_IDS': 'Some selected VMs are invalid or not found',
  'VM_NOT_COMPATIBLE': 'Selected VMs are not compatible with this firewall (different regions)',
  'INSTANCE_NOT_FOUND': 'Instance not found. It may have been deleted',
  'INSTANCE_NOT_AVAILABLE': 'Instance is not available for this operation',
  'INSTANCE_NOT_COMPATIBLE': 'Selected instances are not compatible with this firewall',
  'INSTANCES_NOT_ACTIVE': 'Some instances are not active. Only active instances can be modified',
  'INSTANCE_NOT_ACTIVE': 'Instance must be active to perform this action',
  'INVALID_INSTANCE_ID_FORMAT': 'Invalid instance ID format. Expected format: BRK-XXXXXXD',
  'TIER_NOT_SUPPORTED': 'This operation is only supported for legacy tier instances',
  'REGION_NOT_AVAILABLE': 'Selected region is not available. Please choose a different region',

  'INVALID_REGION_FORMAT': 'Invalid region format. Please try again',
  'UNSUPPORTED_REGION': 'This region is not supported for firewalls',
  'PROTOCOLS_FAILED': 'Unable to load supported protocols. Please refresh the page',
  'REGIONS_FAILED': 'Unable to load available regions. Please refresh the page'
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================




const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return 'Something went wrong. Please try again.';
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// ============================================================================
// RESPONSIVE HOOK
// ============================================================================
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return { isMobile, isTablet };
};

// ============================================================================
// DELETE RULE CONFIRMATION MODAL - ULTRA MINIMAL
// ============================================================================
const DeleteRuleConfirmationModal = ({ isOpen, onClose, onConfirm, rule, isDeleting }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isOpen && rule) {
      logInfo('DeleteRuleConfirmationModal opened', {
        ruleId: rule.id,
        ruleDirection: rule.direction,
        ruleProtocol: rule.protocol
      });
    }

    return () => {
      if (isOpen && rule) {
        logInfo('DeleteRuleConfirmationModal closed', {
          ruleId: rule.id,
          wasConfirmed: false
        });
      }
    };
  }, [isOpen, rule]);

  const handleConfirm = useCallback(() => {
    logUserAction('delete_rule_confirmed', {
      ruleId: rule?.id,
      ruleDirection: rule?.direction,
      ruleProtocol: rule?.protocol
    });
    
    onConfirm();
  }, [onConfirm, rule]);

  const handleClose = useCallback(() => {
    logUserAction('delete_rule_modal_cancelled', {
      ruleId: rule?.id
    });
    
    onClose();
  }, [onClose, rule]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 60,
      padding: '16px',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        backgroundColor: '#111111',
        borderRadius: '8px',
        width: '100%',
        maxWidth: isMobile ? '300px' : '340px',
        border: '1px solid #222',
        padding: isMobile ? '20px' : '24px'
      }}>
        <p style={{
          color: 'white',
          fontSize: '15px',
          margin: '0 0 6px',
          fontWeight: 500
        }}>
          Delete rule?
        </p>
        <p style={{
          color: '#666',
          fontSize: '13px',
          margin: '0 0 20px'
        }}>
          This cannot be undone.
        </p>

        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#222',
              color: '#999',
              fontSize: '13px',
              fontWeight: 500,
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
                e.currentTarget.style.color = '#ccc';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#222';
              e.currentTarget.style.color = '#999';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isDeleting}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid rgba(239,68,68,0.3)',
              backgroundColor: 'rgba(239,68,68,0.1)',
              color: '#ef4444',
              fontSize: '13px',
              fontWeight: 500,
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              opacity: isDeleting ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '80px',
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)';
                e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)';
                e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)';
              }
            }}
          >
            {isDeleting ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid transparent',
                  borderTopColor: '#ef4444',
                  borderBottomColor: '#ef4444',
                  borderRadius: '50%',
                  marginRight: '6px',
                  animation: 'spin 1s linear infinite'
                }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isLoading = false) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '780px',
    height: '580px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '28px',
    borderRight: '1px solid #2a2a2a',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    opacity: isLoading ? 0.6 : 1,
    transition: 'opacity 0.15s',
  },
  rightPanel: {
    width: '240px',
    padding: '28px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    flexShrink: 0,
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: '0 4px 12px rgba(224,234,204,0.3)',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '13px',
  },
  tabContainer: {
    display: 'flex',
    borderBottom: '1px solid #333',
    marginBottom: '16px',
    flexShrink: 0,
  },
  tab: (active) => ({
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: '500',
    color: active ? '#e0eacc' : '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid #e0eacc' : '2px solid transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s',
  }),
  tabCount: (active) => ({
    padding: '2px 6px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: active ? 'rgba(224,234,204,0.2)' : 'rgba(107,114,128,0.2)',
    color: active ? '#e0eacc' : '#6b7280',
  }),
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingRight: '4px',
  },
  ruleCard: {
    padding: '14px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '10px',
    flexShrink: 0,
  },
  ruleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  ruleBadge: (type) => ({
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: type === 'ingress' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(234, 179, 8, 0.15)',
    color: type === 'ingress' ? '#60a5fa' : '#fcd34d',
    border: `1px solid ${type === 'ingress' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(234, 179, 8, 0.4)'}`,
  }),
  deleteBtn: {
    padding: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  ruleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    marginBottom: '4px',
  },
  ruleLabel: {
    color: '#6b7280',
  },
  ruleValue: {
    color: 'white',
    textAlign: 'right',
  },
  vmCard: {
    padding: '14px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  vmName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    margin: 0,
  },
  vmMeta: {
    color: '#6b7280',
    fontSize: '11px',
    margin: '2px 0 0 0',
  },
  statusBadge: (status) => {
    const isSuccess = status === 'SUCCESS';
    const isAttaching = status === 'ATTACHING';
    return {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: isSuccess ? 'rgba(224,234,204,0.15)' : isAttaching ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)',
      color: isSuccess ? '#e0eacc' : isAttaching ? '#fcd34d' : '#ef4444',
      border: `1px solid ${isSuccess ? 'rgba(224,234,204,0.4)' : isAttaching ? 'rgba(234,179,8,0.4)' : 'rgba(239,68,68,0.4)'}`,
    };
  },
  vmStatusBadge: {
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: 'rgba(202,217,238,0.1)',
    color: '#cad9ee',
    border: '1px solid rgba(202,217,238,0.3)',
  },
  summaryHeader: {
    color: '#6b7280',
    fontSize: '11px',
    letterSpacing: '1.5px',
    margin: '0 0 20px 0',
    flexShrink: 0,
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '11px',
    margin: '0 0 4px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryValue: {
    color: 'white',
    fontSize: '14px',
    margin: '0 0 14px 0',
    wordBreak: 'break-all',
  },
  summaryScroll: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: '4px',
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '16px',
  },
  statCard: {
    padding: '12px 8px',
    backgroundColor: '#1a1a1a',
    borderRadius: '8px',
    border: '1px solid #333',
    textAlign: 'center',
  },
  statValue: {
    color: '#e0eacc',
    fontSize: '22px',
    fontWeight: '700',
    margin: 0,
  },
  statLabel: {
    color: '#6b7280',
    fontSize: '10px',
    margin: '4px 0 0 0',
    textTransform: 'uppercase',
  },
  closeBtn: {
    width: '100%',
    marginTop: '16px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    flex: 1,
  },
  emptyTitle: {
    color: 'white',
    fontSize: '15px',
    fontWeight: '500',
    margin: '0 0 8px 0',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '13px',
    margin: 0,
  },
  scrollHint: {
    textAlign: 'center',
    padding: '8px',
    color: '#555',
    fontSize: '11px',
    borderTop: '1px solid #2a2a2a',
    marginTop: '8px',
    flexShrink: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #333',
    borderTopColor: '#e0eacc',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '12px',
  },
  loadingText: {
    color: '#9ca3af',
    fontSize: '14px',
    margin: 0,
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    flexShrink: 0,
  },
  checkIcon: (status) => {
    const isSuccess = status === 'SUCCESS';
    const isAttaching = status === 'ATTACHING';
    return {
      color: isSuccess ? '#22c55e' : isAttaching ? '#fcd34d' : '#ef4444',
    };
  },
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FirewallDetailsModal = ({ isOpen, onClose, firewall, onFirewallUpdated }) => {
  const { handleAuthError } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [firewallDetails, setFirewallDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('rules');
  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showDeleteRuleModal, setShowDeleteRuleModal] = useState(false);
  
  // Refs
  const abortControllerRef = useRef(null);
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  // Derived values
  const styles = getStyles(loading);
  const rules = firewallDetails?.rules || [];
  const attachments = firewallDetails?.attachments || [];
  const isMobileOrTablet = isMobile || isTablet;

  // Sentry context
  useEffect(() => {
    if (isOpen && firewall) {
      Sentry.setContext("firewall-details-modal", {
        firewallId: firewall.id,
        firewallName: firewall.name,
        activeTab,
        ruleCount: rules.length,
        attachmentCount: attachments.length,
        firewallStatus: firewallDetails?.status,
        isLoading: loading,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, firewall, activeTab, rules.length, attachments.length, firewallDetails?.status, loading, isMobile, isTablet]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending firewall details modal requests');
      }
    };
  }, []);
const debouncedFetchFirewallDetails = useCallback(
  debounce(async (id) => {
    const startTime = performance.now();
    
    logInfo('Initiating firewall details fetch', { firewallId: id, activeTab });

    if (retryAttempts.current >= maxRetries) {
      setError('Maximum refresh attempts reached. Please wait before trying again.');
      logError('Maximum firewall details fetch attempts exceeded', {
        attempts: retryAttempts.current,
        firewallId: id
      });
      return;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    retryAttempts.current++;
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getFirewallDetails(id);
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (abortControllerRef.current?.signal.aborted) {
        logInfo('Firewall details fetch request was aborted', { duration });
        return;
      }
      
      if (response.success) {
        setFirewallDetails(response.firewall);
        retryAttempts.current = 0;

        logPerformance('fetch_firewall_details', duration, {
          firewallId: id,
          ruleCount: response.firewall?.rules?.length || 0,
          attachmentCount: response.firewall?.attachments?.length || 0,
          success: true
        });

        logInfo('Firewall details loaded successfully', {
          firewallId: id,
          firewallName: response.firewall?.name,
          ruleCount: response.firewall?.rules?.length || 0,
          attachmentCount: response.firewall?.attachments?.length || 0,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        
        logError('Failed to fetch firewall details', {
          firewallId: id,
          code: response?.code,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        logInfo('Firewall details fetch request aborted by user');
        return;
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'AUTH_ERROR') {
        logError('Authentication error during firewall details fetch', {
          firewallId: id,
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        });
        handleAuthError();
        return;
      }
      
      // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('Firewall details fetch error', {
          firewallId: id,
          code: err.code,
          status: err.status,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 3: Use interceptor's user-friendly message directly
      if (err.error) {
        setError(err.error);
        logError('Firewall details fetch error', {
          firewallId: id,
          code: err.code,
          status: err.status,
          message: err.error,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 4: Fallback for unexpected error format
      setError('Something went wrong. Please try again.');
      logError('Firewall details fetch error (unexpected format)', {
        firewallId: id,
        errorType: typeof err,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoading(false);
    }
  }, 300),
  [activeTab, handleAuthError]
);
  // Initialize on modal open
  useEffect(() => {
    if (isOpen && firewall && firewall.id) {
      logInfo('Firewall details modal opened', {
        firewallId: firewall.id,
        firewallName: firewall.name,
        isMobile,
        isTablet
      });

      logUserAction('firewall_details_modal_opened', {
        firewallId: firewall.id,
        firewallName: firewall.name
      });

      setActiveTab('rules');
      setError(null);
      debouncedFetchFirewallDetails(firewall.id);
    }
  }, [isOpen, firewall, debouncedFetchFirewallDetails, isMobile, isTablet]);

  // Open delete rule confirmation modal
  const handleDeleteRuleClick = useCallback((rule) => {
    logUserAction('firewall_rule_delete_initiated', { firewallId: firewall?.id, ruleId: rule.id });
    setRuleToDelete(rule);
    setShowDeleteRuleModal(true);
  }, [firewall]);

  // Close delete rule confirmation modal
  const handleDeleteRuleModalClose = useCallback(() => {
    logUserAction('firewall_rule_delete_cancelled', { firewallId: firewall?.id, ruleId: ruleToDelete?.id });
    setRuleToDelete(null);
    setShowDeleteRuleModal(false);
  }, [firewall, ruleToDelete]);


const handleDeleteRuleConfirm = useCallback(async () => {
  if (!ruleToDelete) return;
  
  const ruleId = ruleToDelete.id;
  const startTime = performance.now();
  
  logUserAction('firewall_rule_delete_confirmed', { firewallId: firewall?.id, ruleId });
  
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  setDeletingRuleId(ruleId);
  setError(null);
  
  try {
    const response = await api.removeFirewallRule(firewall.id, ruleId);
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (abortControllerRef.current?.signal.aborted) {
      logInfo('Firewall rule deletion request was aborted', { duration });
      return;
    }
    
    if (response.success) {
      setShowDeleteRuleModal(false);
      setRuleToDelete(null);
      debouncedFetchFirewallDetails(firewall.id);
      if (onFirewallUpdated) onFirewallUpdated();

      logPerformance('delete_firewall_rule', duration, { firewallId: firewall.id, ruleId, success: true });
      logInfo('Firewall rule deleted successfully', { firewallId: firewall.id, ruleId, duration: `${duration.toFixed(2)}ms` });
      logUserAction('firewall_rule_delete_success', { firewallId: firewall.id, ruleId });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      setError(safeMessage);
      
      logError('Failed to delete firewall rule', { 
        firewallId: firewall.id, 
        ruleId, 
        code: response?.code,
        duration: `${duration.toFixed(2)}ms` 
      });
      logUserAction('firewall_rule_delete_failed', { firewallId: firewall.id, ruleId });
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      logInfo('Firewall rule deletion request aborted by user');
      return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      logError('Authentication error during firewall rule deletion', {
        firewallId: firewall.id,
        ruleId,
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      handleAuthError();
      return;
    }
    
    // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Firewall rule delete error', {
        firewallId: firewall.id,
        ruleId,
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Use interceptor's user-friendly message directly
    if (err.error) {
      setError(err.error);
      logError('Firewall rule delete error', {
        firewallId: firewall.id,
        ruleId,
        code: err.code,
        status: err.status,
        message: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Fallback for unexpected error format
    setError('Something went wrong. Please try again.');
    logError('Firewall rule delete error (unexpected format)', {
      firewallId: firewall.id,
      ruleId,
      errorType: typeof err,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    logUserAction('firewall_rule_delete_error', { firewallId: firewall.id, ruleId });
  } finally {
    setDeletingRuleId(null);
    setShowDeleteRuleModal(false);
    setRuleToDelete(null);
  }
}, [firewall, ruleToDelete, debouncedFetchFirewallDetails, onFirewallUpdated, handleAuthError]);
// Protocol name mapping
  const getProtocolName = useCallback((protocol) => {
    const protocolMap = {
      'tcp': 'TCP',
      'udp': 'UDP',
      'icmp': 'ICMP',
      'ipv6-icmp': 'IPv6 ICMP',
      'any': 'Any'
    };
    const sanitizedProtocol = sanitizeText(protocol);
    return protocolMap[sanitizedProtocol] || sanitizedProtocol.toUpperCase();
  }, []);

  // Direction formatting
  const formatDirection = useCallback((direction) => {
    const sanitizedDirection = sanitizeText(direction);
    return sanitizedDirection.charAt(0).toUpperCase() + sanitizedDirection.slice(1);
  }, []);

  // Tab change handler
  const handleTabChange = useCallback((tab) => {
    logUserAction('firewall_details_tab_changed', { firewallId: firewall?.id, fromTab: activeTab, toTab: tab });
    logInfo('Firewall details tab changed', { firewallId: firewall?.id, fromTab: activeTab, toTab: tab });
    setActiveTab(tab);
  }, [activeTab, firewall?.id]);

  // Close handler
  const handleClose = useCallback(() => {
    logUserAction('firewall_details_modal_closed', {
      firewallId: firewall?.id,
      activeTab,
      viewedRuleCount: rules.length,
      viewedAttachmentCount: attachments.length
    });
    
    logInfo('Firewall details modal closed', {
      firewallId: firewall?.id,
      activeTab,
      ruleCount: rules.length,
      attachmentCount: attachments.length
    });
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    onClose();
  }, [firewall?.id, activeTab, rules.length, attachments.length, onClose]);

  // Get modal styles based on device
  const getModalStyles = useCallback(() => {
    if (isMobile) {
      return {
        width: 'calc(100% - 1rem)',
        maxWidth: '100%',
        height: 'auto',
        maxHeight: '90vh',
        margin: '0.5rem',
        flexDirection: 'column',
      };
    } else if (isTablet) {
      return {
        width: 'calc(100% - 2rem)',
        maxWidth: '700px',
        height: 'auto',
        maxHeight: '85vh',
        margin: '1rem',
        flexDirection: 'column',
      };
    }
    return {};
  }, [isMobile, isTablet]);

  if (!isOpen) return null;

  // Display values
  const firewallName = firewallDetails?.name || firewall?.name || '';
  const firewallRegion = firewallDetails?.region || firewall?.region || '';
  const firewallStatus = firewallDetails?.status || firewall?.status || '';
  const firewallDescription = firewallDetails?.description || firewall?.description || '';
  const firewallId = firewallDetails?.id || firewall?.id || '';
  const firewallCreatedAt = firewallDetails?.created_at || firewall?.created_at || '';

  const modalStyles = getModalStyles();

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .firewall-details-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .firewall-details-scrollbar::-webkit-scrollbar-track {
          background: #2a2a2a;
          border-radius: 3px;
        }
        .firewall-details-scrollbar::-webkit-scrollbar-thumb {
          background: #555;
          border-radius: 3px;
        }
        .firewall-details-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      `}</style>
      
      <div style={styles.overlay}>
        <div style={{ ...styles.modal, ...modalStyles }}>
          {/* Mobile/Tablet Header */}
          {isMobileOrTablet && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #2a2a2a',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <FaNetworkWired size={18} style={{ color: '#1a1a1a' }} />
                </div>
                <div>
                  <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600' }}>{sanitizeText(firewallName)}</h2>
                  <p style={{ color: '#6b7280', margin: 0, fontSize: '12px' }}>{sanitizeText(firewallRegion)}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '8px',
                }}
              >
                <FaTimes size={20} />
              </button>
            </div>
          )}

          {/* LEFT PANEL */}
          <div style={{
            ...styles.leftPanel,
            borderRight: isMobileOrTablet ? 'none' : '1px solid #2a2a2a',
            padding: isMobileOrTablet ? '20px' : '28px',
            flex: isMobileOrTablet ? 'none' : 1,
            maxHeight: isMobileOrTablet ? 'calc(90vh - 160px)' : 'none',
            overflowY: isMobileOrTablet ? 'auto' : 'hidden',
          }}>
            {/* Desktop Header */}
            {!isMobileOrTablet && (
              <div style={styles.header}>
                <div style={styles.iconWrapper}>
                  <FaNetworkWired size={22} style={{ color: '#1a1a1a' }} />
                </div>
                <div>
                  <h2 style={styles.title}>{sanitizeText(firewallName)}</h2>
                  <p style={styles.subtitle}>Firewall configuration & rules</p>
                </div>
              </div>
            )}
            
            {/* Error Display */}
            {error && (
              <div style={styles.errorBox}>
                <FaExclamationTriangle size={16} style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
                <span style={{ color: '#fca5a5', fontSize: '13px' }}>{sanitizeText(error)}</span>
              </div>
            )}

            {/* Mobile/Tablet: Stats Grid */}
            {isMobileOrTablet && !loading && firewallDetails && (
              <div style={{ ...styles.statGrid, marginBottom: '16px' }}>
                <div style={styles.statCard}>
                  <p style={styles.statValue}>{rules.length}</p>
                  <p style={styles.statLabel}>Rules</p>
                </div>
                <div style={styles.statCard}>
                  <p style={styles.statValue}>{attachments.length}</p>
                  <p style={styles.statLabel}>VMs</p>
                </div>
              </div>
            )}
            
            {/* Tabs */}
            <div style={styles.tabContainer}>
              <button style={styles.tab(activeTab === 'rules')} onClick={() => handleTabChange('rules')}>
                <FaShieldAlt size={14} />
                Rules
                <span style={styles.tabCount(activeTab === 'rules')}>{rules.length}</span>
              </button>
              <button style={styles.tab(activeTab === 'attachments')} onClick={() => handleTabChange('attachments')}>
                <FaServer size={14} />
                Attached VMs
                <span style={styles.tabCount(activeTab === 'attachments')}>{attachments.length}</span>
              </button>
            </div>
            
            {/* Tab Content */}
            {loading && !firewallDetails ? (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner} />
                <p style={styles.loadingText}>Loading firewall details...</p>
              </div>
            ) : (
              <>
                <div style={styles.contentArea} className="firewall-details-scrollbar">
                  {activeTab === 'rules' ? (
                    rules.length > 0 ? (
                      rules.map(rule => (
                        <div key={rule.id} style={styles.ruleCard}>
                          <div style={styles.ruleHeader}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={styles.ruleBadge(rule.direction)}>
                                {formatDirection(rule.direction)}
                              </span>
                              <span style={{ color: 'white', fontSize: '13px', fontWeight: '500' }}>
                                {getProtocolName(rule.protocol)}
                              </span>
                            </div>
                            <button
                              style={{
                                ...styles.deleteBtn,
                                opacity: deletingRuleId === rule.id ? 0.5 : 1,
                                cursor: deletingRuleId === rule.id ? 'not-allowed' : 'pointer',
                              }}
                              onClick={() => handleDeleteRuleClick(rule)}
                              disabled={deletingRuleId === rule.id}
                              onMouseEnter={(e) => {
                                if (deletingRuleId !== rule.id) {
                                  e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {deletingRuleId === rule.id ? (
                                <div style={{ width: '12px', height: '12px', border: '2px solid #ef4444', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </button>
                          </div>
                          <div style={styles.ruleRow}>
                            <span style={styles.ruleLabel}>Ethertype</span>
                            <span style={styles.ruleValue}>{sanitizeText(rule.ethertype)}</span>
                          </div>
                          <div style={styles.ruleRow}>
                            <span style={styles.ruleLabel}>Port Range</span>
                            <span style={styles.ruleValue}>
                              {rule.port_range_min && rule.port_range_max 
                                ? `${sanitizeText(String(rule.port_range_min))} - ${sanitizeText(String(rule.port_range_max))}` 
                                : 'All Ports'}
                            </span>
                          </div>
                          <div style={styles.ruleRow}>
                            <span style={styles.ruleLabel}>Remote IP</span>
                            <span style={styles.ruleValue}>{sanitizeText(rule.remote_ip_prefix)}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={styles.emptyState}>
                        <FaShieldAlt size={40} style={{ color: '#555', marginBottom: '16px' }} />
                        <p style={styles.emptyTitle}>No Rules Configured</p>
                        <p style={styles.emptyText}>Add inbound or outbound rules to control traffic flow.</p>
                      </div>
                    )
                  ) : (
                    attachments.length > 0 ? (
                      attachments.map(attachment => (
                        <div key={attachment.id} style={styles.vmCard}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              backgroundColor: attachment.status === 'SUCCESS' ? 'rgba(34,197,94,0.15)' : attachment.status === 'ATTACHING' ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <FaServer size={14} style={styles.checkIcon(attachment.status)} />
                            </div>
                            <div>
                              <p style={styles.vmName}>{sanitizeText(attachment.vm?.name) || 'Unknown'}</p>
                              <p style={styles.vmMeta}>
                                <span style={styles.vmStatusBadge}>{sanitizeText(attachment.vm?.status) || 'Unknown'}</span>
                              </p>
                            </div>
                          </div>
                          <span style={styles.statusBadge(attachment.status)}>
                            {sanitizeText(attachment.status)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={styles.emptyState}>
                        <FaServer size={40} style={{ color: '#555', marginBottom: '16px' }} />
                        <p style={styles.emptyTitle}>No VMs Attached</p>
                        <p style={styles.emptyText}>Attach this firewall to virtual machines to apply rules.</p>
                      </div>
                    )
                  )}
                </div>
                
                {/* Scroll hint */}
                {((activeTab === 'rules' && rules.length > 3) || (activeTab === 'attachments' && attachments.length > 4)) && (
                  <div style={styles.scrollHint}>
                    ↓ Scroll for more ({activeTab === 'rules' ? rules.length : attachments.length} items)
                  </div>
                )}
              </>
            )}

            {/* Mobile/Tablet: Close Button */}
            {isMobileOrTablet && (
              <button
                style={{
                  ...styles.closeBtn,
                  marginTop: '20px',
                }}
                onClick={handleClose}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#444'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#333'}
              >
                Close
              </button>
            )}
          </div>
          
          {/* RIGHT PANEL - Desktop Only */}
          {!isMobileOrTablet && (
            <div style={styles.rightPanel}>
              <h3 style={styles.summaryHeader}>FIREWALL</h3>
              
              <div style={styles.summaryScroll} className="firewall-details-scrollbar">
                <p style={styles.summaryLabel}>Name</p>
                <p style={styles.summaryValue}>{sanitizeText(firewallName)}</p>
                
                <p style={styles.summaryLabel}>Region</p>
                <p style={{ ...styles.summaryValue, color: '#e0eacc' }}>{sanitizeText(firewallRegion)}</p>
                
                <p style={styles.summaryLabel}>Status</p>
                <p style={{ ...styles.summaryValue, marginBottom: '20px' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: 'rgba(202,217,238,0.1)',
                    color: '#cad9ee',
                    border: '1px solid rgba(202,217,238,0.3)',
                  }}>
                    {sanitizeText(firewallStatus)}
                  </span>
                </p>
                
                {/* Stats Grid */}
                <div style={styles.statGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{rules.length}</p>
                    <p style={styles.statLabel}>Rules</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{attachments.length}</p>
                    <p style={styles.statLabel}>VMs</p>
                  </div>
                </div>
                
                {firewallDescription && (
                  <>
                    <p style={styles.summaryLabel}>Description</p>
                    <p style={{ 
                      ...styles.summaryValue, 
                      fontSize: '12px', 
                      color: '#9ca3af',
                      lineHeight: '1.5',
                    }}>
                      {sanitizeText(firewallDescription)}
                    </p>
                  </>
                )}
                
                <p style={styles.summaryLabel}>Firewall ID</p>
                <p style={{ 
                  ...styles.summaryValue, 
                  fontSize: '11px', 
                  color: '#6b7280',
                  fontFamily: 'monospace',
                }}>
                  {sanitizeText(firewallId)}
                </p>
                
                <p style={styles.summaryLabel}>Created</p>
                <p style={{ ...styles.summaryValue, fontSize: '12px', color: '#9ca3af' }}>
                  {firewallCreatedAt ? new Date(firewallCreatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              
              <button
                style={styles.closeBtn}
                onClick={handleClose}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#444'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#333'}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Rule Confirmation Modal */}
      <DeleteRuleConfirmationModal
        isOpen={showDeleteRuleModal}
        onClose={handleDeleteRuleModalClose}
        onConfirm={handleDeleteRuleConfirm}
        rule={ruleToDelete}
        isDeleting={deletingRuleId === ruleToDelete?.id}
      />
    </>
  );
};

export default FirewallDetailsModal;